import { useMemo } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

interface PreviewData {
  title: string;
  subtitle: string;
  body: string;
  tags: string[];
  readingTime: number;
  heroImageUrl: string;
  heroImageFile: File | null;
}

interface Props {
  data: PreviewData;
  onClose: () => void;
}

const PROSE_CLASSES = cn(
  '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3',
  '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-3',
  '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2',
  '[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-2',
  '[&_p]:my-3 [&_p]:leading-relaxed [&_p]:text-slate-700',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3',
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3',
  '[&_li]:my-1 [&_li]:text-slate-700',
  '[&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800',
  '[&_strong]:font-semibold [&_strong]:text-slate-900',
  '[&_em]:italic',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_blockquote]:my-4',
  '[&_code]:bg-slate-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm [&_code]:text-slate-800',
  '[&_pre]:bg-slate-900 [&_pre]:text-slate-100 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-4 [&_pre]:text-sm',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-slate-100',
  '[&_img]:max-w-full [&_img]:rounded [&_img]:my-4',
  '[&_hr]:my-6 [&_hr]:border-slate-200',
);

export function BlogPostPreviewModal({ data, onClose }: Props) {
  const heroSrc = useMemo(() => {
    if (data.heroImageFile) return URL.createObjectURL(data.heroImageFile);
    return data.heroImageUrl || '';
  }, [data.heroImageFile, data.heroImageUrl]);

  return (
    <Modal isOpen onClose={onClose} title="Preview" size="xxl">
      <article className="max-w-3xl mx-auto">
        {heroSrc && (
          <img
            src={heroSrc}
            alt=""
            className="w-full max-h-96 object-cover rounded-lg mb-6"
          />
        )}

        <header className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900 leading-tight">
            {data.title || 'Untitled post'}
          </h1>
          {data.subtitle && (
            <p className="mt-3 text-xl text-slate-600 leading-snug">{data.subtitle}</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>{Math.max(1, data.readingTime)} min read</span>
            {data.tags.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <div className="flex flex-wrap gap-1">
                  {data.tags.map((t) => (
                    <Badge key={t} variant="neutral" size="sm">
                      {t}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>

        {data.body ? (
          <div
            className={PROSE_CLASSES}
            dangerouslySetInnerHTML={{ __html: data.body }}
          />
        ) : (
          <p className="text-slate-400 italic">No body content yet.</p>
        )}
      </article>
    </Modal>
  );
}
